import { Button, Card, Flex, Heading } from '@utilitywarehouse/hearth-react';
import LogoFullPurple from '@utilitywarehouse/hearth-svg-assets/lib/logo-full-purple.svg';
import clsx from 'clsx';
import { FC } from 'react';

import { getPathTo } from '@features/Remote/Interaction/getPathTo';
import { useSendClickInteraction } from '@features/Remote/Interaction/useInteractions';

import './styles.scss';

export type ButtonType = 'slide' | 'external' | 'quote';

export interface CallToActionSlideButton {
  id: string;
  label: string;
  type: ButtonType;
  slideIndex?: number;
  externalUrl?: string;
}

export interface CallToActionSlideProps {
  heading: string;
  buttons: CallToActionSlideButton[];
  onSlideChange?: (index: number) => void;
  onQuoteClick?: () => void;
  className?: string;
  canUpdateState?: boolean;
}

const CallToActionSlide: FC<CallToActionSlideProps> = ({
  heading,
  buttons,
  onSlideChange,
  onQuoteClick,
  className,
  canUpdateState = true,
}) => {
  const sendInteraction = useSendClickInteraction();
  const handleButtonClick = (button: CallToActionSlideButton) => {
    if (!canUpdateState) return;

    switch (button.type) {
      case 'slide':
        if (button.slideIndex !== undefined && onSlideChange) {
          onSlideChange(button.slideIndex);
        }
        break;
      case 'external':
        if (button.externalUrl) {
          const externalButton = document.getElementById(button.id);
          if (externalButton) {
            sendInteraction(getPathTo(externalButton));
          }
          window.open(button.externalUrl, '_blank');
        }
        break;
      case 'quote':
        if (onQuoteClick) {
          onQuoteClick();
        }
        break;
    }
  };

  return (
    <Card
      direction="column"
      alignItems="center"
      justifyContent="center"
      className={clsx('call-to-action-slide', className)}
      borderRadius="xl"
      gap="100"
      shadowColor="functional"
      variant="emphasis"
    >
      <img
        src={LogoFullPurple}
        alt="Utility Warehouse logo"
        className="content-slide__logo"
      />

      <Flex gap="500" justifyContent="center" direction="column">
        <Heading as="h1" size="xl" className="content-slide__heading">
          {heading}
        </Heading>
        <Flex
          gap="100"
          justifyContent="center"
          wrap="wrap"
          className="content-slide__buttons"
        >
          {buttons.map((button, index) => (
            <Button
              id={button.id}
              key={index}
              variant="emphasis"
              className="content-slide__button"
              onClick={() => handleButtonClick(button)}
              disabled={!canUpdateState}
            >
              {button.label}
            </Button>
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};

export { CallToActionSlide };
